import React from "react";
import { motion } from "framer-motion";

/* About-page-specific variant of the Trusted Components strip:
   Same 9 brand logos, same card styling — different heading + subtitle
   and NO bottom CTA (the page already has its own final CTA banner). */

const BRANDS = [
  { name: "Bonfiglioli",        src: "/components/brand-bonfiglioli.png" },
  { name: "ABB",                src: "/components/brand-abb.png" },
  { name: "Schneider Electric", src: "/components/brand-schneider.png" },
  { name: "Usha Martin",        src: "/components/brand-usha-martin.png" },
  { name: "FAG",                src: "/components/brand-fag.png" },
  { name: "Elecon",             src: "/components/brand-elecon.png" },
  { name: "Polycab",            src: "/components/brand-polycab.png" },
  { name: "Kirloskar Engines",  src: "/components/brand-kirloskar.png" },
  { name: "Crompton",           src: "/components/brand-crompton.png" },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, ease: "easeOut" } },
};

export default function TrustedComponentsAbout() {
  return (
    <section
      data-testid="about-trusted-components"
      className="relative bg-[#0B0B0B] py-24 md:py-32"
    >
      {/* Thin orange accent line at the top of the section */}
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-[2px]"
        style={{ background: "#FF6A00" }}
      />

      <div className="container-x">
        {/* Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          className="max-w-3xl"
        >
          <div className="heading-eyebrow mb-4">Trusted Components</div>
          <h2
            data-testid="about-trusted-heading"
            className="font-display uppercase text-liftile-cream leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl"
          >
            Powered by <span className="text-liftile-orange">Industry-Leading Components</span>
          </h2>
          <p className="font-body text-liftile-cream/65 mt-6 text-base md:text-lg leading-relaxed max-w-2xl">
            Every LIFTILE machine is assembled using carefully selected
            components from globally recognized manufacturers to deliver
            reliable performance, safety and long-term durability.
          </p>
        </motion.div>

        {/* Logo grid — 2 cols mobile, 3 tablet & desktop (perfect 3×3) */}
        <div
          data-testid="about-trusted-grid"
          className="mt-14 md:mt-20 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 max-w-5xl mx-auto"
        >
          {BRANDS.map((brand, i) => (
            <motion.div
              key={brand.name}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              transition={{ delay: i * 0.05 }}
              data-testid={`about-trusted-card-${brand.name.toLowerCase().replace(/\s+/g, "-")}`}
              className="group relative bg-white rounded-xl shadow-[0_4px_18px_-8px_rgba(0,0,0,0.55)] hover:shadow-[0_18px_45px_-12px_rgba(255,107,26,0.45)] border-2 border-transparent hover:border-liftile-orange transition-all duration-400 ease-out hover:-translate-y-1.5 aspect-[5/3] flex items-center justify-center p-7"
              title={brand.name}
            >
              <img
                src={brand.src}
                alt={`${brand.name} logo`}
                loading="lazy"
                className="max-w-full max-h-16 md:max-h-20 object-contain transition-transform duration-500 group-hover:scale-105"
              />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
